import React from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { Company } from "../types/auth";
import SettingsForm from "./SettingsForm";
import { SETTINGS_KEYS } from "./HierarchyManagementConstants";
import type { WizardData } from "./HierarchyManagement";

interface CompanyStepProps {
  modalAction: "create" | "edit" | "assign-company";
  wizardData: WizardData;
  setWizardData: React.Dispatch<React.SetStateAction<WizardData>>;
  companyMode: "attach" | "create";
  setCompanyMode: React.Dispatch<React.SetStateAction<"attach" | "create">>;
  companyOverride: Record<string, boolean>;
  setCompanyOverride: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  availableCompanies: Company[];
}

const CompanyStep: React.FC<CompanyStepProps> = ({
  modalAction,
  wizardData,
  setWizardData,
  companyMode,
  setCompanyMode,
  companyOverride,
  setCompanyOverride,
  availableCompanies,
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'companyStep' });
  return (
    <>
      {modalAction !== "edit" && (
        <>
          <h3 className="an-wizard-step-title">{t('companyAssignment')}</h3>
          <Form.Group className="mb-3">
            <Form.Label>{t('mode')}</Form.Label>
            <Form.Select
              value={companyMode}
              onChange={(e) =>
                setCompanyMode(e.target.value as "attach" | "create")
              }
            >
              <option value="attach">{t('attachExisting')}</option>
              <option value="create">{t('createNew')}</option>
            </Form.Select>
          </Form.Group>
          {companyMode === "attach" && (
            <Form.Group className="mb-3">
              <Form.Label>{t('selectCompany')}</Form.Label>
              <Form.Select
                value={wizardData.company_id || ""}
                onChange={(e) =>
                  setWizardData((d: WizardData) => ({ ...d, company_id: e.target.value }))
                }
              >
                <option value="">{t('selectCompanyPlaceholder')}</option>
                {availableCompanies.map((c) => (
                  <option key={c.id} value={c.company_id}>
                    {c.name} ({c.company_id})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </>
      )}

      {(modalAction === "edit" || companyMode === "create") && (
        <>
          {modalAction === "edit" && <h3 className="an-wizard-step-title">{t('editCompanyDetails')}</h3>}
          <Form.Group className="mb-3">
            <Form.Label>{t('companyId')}</Form.Label>
            <Form.Control
              type="text"
              value={wizardData.company_id || ""}
              disabled={modalAction === "edit"}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({ ...d, company_id: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('companyName')}</Form.Label>
            <Form.Control
              type="text"
              value={wizardData.company_name || ""}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({
                  ...d,
                  company_name: e.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('status')}</Form.Label>
            <Form.Select
              value={wizardData.status || 1}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({
                  ...d,
                  status: Number(e.target.value) as 1 | 2,
                }))
              }
            >
              <option value={1}>{t('active')}</option>
              <option value={2}>{t('inactive')}</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('email')}</Form.Label>
            <Form.Control
              type="email"
              value={wizardData.email || ""}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({ ...d, email: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('phone')}</Form.Label>
            <Form.Control
              type="text"
              value={wizardData.phone || ""}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({ ...d, phone: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('description')}</Form.Label>
            <Form.Control
              as="textarea"
              value={wizardData.description || ""}
              onChange={(e) =>
                setWizardData((d: WizardData) => ({ ...d, description: e.target.value }))
              }
            />
          </Form.Group>
        </>
      )}
      <div className={`mt-3 an-company-step-panel ${modalAction != "edit" ? "border-top pt-3" : ""}`}>
        <h6 className="fw-bold mb-3">{t('companySettingsOverrides')}</h6>
        <SettingsForm
          settings={SETTINGS_KEYS}
          wizardData={wizardData}
          setWizardData={setWizardData}
          override={companyOverride}
          setOverride={setCompanyOverride}
          prefix="company"
          parentData={{
            timezone: wizardData.location_timezone,
            address: wizardData.location_address,
            repo_th: wizardData.location_repo_th,
            similarity_addition: wizardData.location_similarity_addition,
            valid_th_from: wizardData.location_valid_th_from,
            valid_th_to: wizardData.location_valid_th_to,
            ppe_types: wizardData.location_ppe_types,
            ppe_min_confidence: wizardData.location_ppe_min_confidence,
            shifts_enabled: wizardData.location_shifts_enabled,
            payroll_enabled: wizardData.location_payroll_enabled,
            shift_auto_detect: wizardData.location_shift_auto_detect,
          }}
        />
      </div>
    </>
  );
};

export default CompanyStep;